import { HStack, Stack } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import { useRef, useState } from 'react';

import { locales, useLanguageSwitcher } from '#features/i18n';
import { Button } from '#features/ui';
import { GameParameterSelect } from '#features/ui/components/inputs/GameParameterSelect';

type Locale = keyof typeof locales;
type LanguageSwitcherProps = {
  presentation?: 'buttons' | 'picker';
  compact?: boolean;
  disabled?: boolean;
};
type LanguageSwitcherViewProps = LanguageSwitcherProps & {
  currentLocale: Locale;
  pendingLocale?: Locale | null;
  onChange: (locale: Locale) => void;
};

/** Shared presentation; previews can exercise it without writing language preferences. */
export const LanguageSwitcherView = ({
  presentation = 'buttons',
  compact = true,
  disabled = false,
  currentLocale,
  pendingLocale = null,
  onChange,
}: LanguageSwitcherViewProps) => {
  const { t } = useLingui();
  const blocked = disabled || pendingLocale !== null;
  const selectLocale = (value: unknown) => {
    if (
      blocked ||
      value === currentLocale ||
      typeof value !== 'string' ||
      !Object.keys(locales).includes(value)
    )
      return;
    onChange(value as Locale);
  };

  if (presentation === 'picker') {
    return (
      <Stack w="32" maxW="full" mx="auto">
        <GameParameterSelect
          name="locale"
          triggerLabel={t`Language`}
          triggerVariant="ghost"
          value={currentLocale}
          disabled={blocked}
          searchable={false}
          onChange={selectLocale}
          options={Object.values(locales).map(({ code, nativeName }) => ({
            value: code,
            label: nativeName,
          }))}
        />
      </Stack>
    );
  }

  return (
    <HStack gap={2} flexShrink={0} w="max-content" role="group" aria-label={t`Language`}>
      {Object.entries(locales).map(([code, { nativeName }]) => {
        const active = code === currentLocale;
        const label = compact ? code.toUpperCase() : nativeName;
        return (
          <Button
            key={code}
            type="button"
            size="lg"
            variant={active ? 'outline' : 'ghost'}
            colorPalette="gray"
            color={active ? 'fg' : 'fg.muted'}
            cursor={active ? 'default' : 'pointer'}
            {...(compact ? { w: 11, px: 0 } : {})}
            aria-label={label}
            aria-pressed={active}
            lang={code}
            disabled={blocked}
            loading={pendingLocale === code}
            onClick={() => selectLocale(code)}
          >
            {label}
          </Button>
        );
      })}
    </HStack>
  );
};

export const LanguageSwitcher = (props: LanguageSwitcherProps) => {
  const { currentLocale, changeLocale } = useLanguageSwitcher();
  const [pendingLocale, setPendingLocale] = useState<Locale | null>(null);
  const changing = useRef(false);

  const selectLocale = async (locale: Locale) => {
    if (props.disabled || changing.current || locale === currentLocale) return;
    changing.current = true;
    setPendingLocale(locale);
    try {
      await changeLocale(locale);
    } finally {
      changing.current = false;
      setPendingLocale(null);
    }
  };

  return (
    <LanguageSwitcherView
      {...props}
      currentLocale={currentLocale}
      pendingLocale={pendingLocale}
      onChange={selectLocale}
    />
  );
};
